import React, { useCallback, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import { Button } from 'components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';

import { TAB_TRIGGER_STYLES } from '../integration-test-modal.constants';
import { FormValues, RequestHeaders } from '../integration-test-modal.types';

import { CodeBlock } from './CodeBlock';
import { InfoNote } from './InfoNote';
import { StackedCard } from './StackedCard';

enum RequestTab {
  HEADERS = 'headers',
  BODY = 'body'
}

interface IRequestPanelProps {
  url: string;
  headers: RequestHeaders;
  body: FormValues;
  className?: string;
}

export const RequestPanel: React.FC<IRequestPanelProps> = ({
  url,
  headers,
  body,
  className
}) => {
  const [activeTab, setActiveTab] = useState<RequestTab>(RequestTab.BODY);

  const handleTabChange = useCallback((value: string): void => {
    setActiveTab(value as RequestTab);
  }, []);

  const handleCopyCurl = useCallback((): void => {
    const headersString = Object.entries(headers)
      .map(([key, value]) => `-H "${key}: ${value}"`)
      .join(' ');
    const bodyString = Object.keys(body).length > 0 ? `-d '${JSON.stringify(body)}'` : '';
    const curlCommand = `curl -X POST ${headersString} ${bodyString} "${url}"`;
    navigator.clipboard.writeText(curlCommand);
  }, [url, headers, body]);

  const copyCurlButton = useMemo(
    () => (
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCopyCurl}
        className="flex items-center gap-1.5 text-p-xs text-base-mutedForeground hover:text-base-foreground"
      >
        Copy cURL
        <FontAwesomeIcon icon="fa-regular fa-copy" className="h-3.5 w-3.5" />
      </Button>
    ),
    [handleCopyCurl]
  );

  return (
    <StackedCard title="Request" titleRightContent={copyCurlButton} className={className}>
      <div className="space-y-4">
        <div>
          <label className="block text-p-xs text-base-mutedForeground mb-1.5">URL</label>
          <div className="rounded-lg border border-base-border bg-base-muted px-3 py-2 text-p-s text-base-mutedForeground">
            {url}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="bg-transparent border-b border-base-border rounded-none w-full justify-start gap-4 h-auto p-0">
            <TabsTrigger value={RequestTab.HEADERS} className={TAB_TRIGGER_STYLES}>
              Headers
            </TabsTrigger>
            <TabsTrigger value={RequestTab.BODY} className={TAB_TRIGGER_STYLES}>
              Body
            </TabsTrigger>
          </TabsList>

          <TabsContent value={RequestTab.HEADERS} className="mt-4">
            <CodeBlock content={headers} className="min-h-[140px]" />
          </TabsContent>

          <TabsContent value={RequestTab.BODY} className="mt-4">
            <CodeBlock content={body} className="min-h-[140px]" />
          </TabsContent>
        </Tabs>

        <InfoNote>
          Pass <code className="font-mono bg-base-muted px-1 rounded">is-test: true</code> to
          classify the data as test data.
        </InfoNote>
      </div>
    </StackedCard>
  );
};

